import {
  AddOutlined as AddOutlinedIcon,
  BadgeOutlined as BadgeOutlinedIcon,
  BuildOutlined as BuildOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  RemoveOutlined as RemoveOutlinedIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Popover,
  Popper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { FormControl } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers-6/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers-6/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers-6/LocalizationProvider";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers-6/PickersDay";
import {
  differenceInDays,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfDay,
} from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import useWorkerSchedulerData from "../../../../components/full-time-worker-scheduler/controllers/use-worker-scheduler-data";
import {
  fetchWorkerCallOffs,
  resetUpdateWorkerAvailabilityStatus,
  updateWorkerAvailabilityStatus,
} from "../../../../components/full-time-worker-scheduler/store/worker-scheduler-slice";
import CallOffSection from "../../../../components/full-time-worker-scheduler/ui/dialogs/call-off-dialog/call-off-section";
import Highlight from "../../../../components/Highlight";
import colors from "../../../../config/colors";
import { generateDurationOptionsByMinutes } from "../../../../features/web-worker-app/LeaveRequestPage";
import {
  formatLeaveTypeDisplay,
  getHoursAndMinutesStringFromFloat,
} from "../../../../features/web-worker-app/util/helper";
import axios from "../../../../utils/api-client";
import {
  useDateFnsLocale,
  useDatePickerLocale,
} from "../../../../utils/date-locale";
import { StoreType } from "../../../../utils/types";
import { fetchFiltersForUser } from "../../store/worker-requests-slice";

interface WorkerFTShift {
  workerId: string;
  shiftId: string;
  startDateTime: string;
  endDateTime: string;
  uPatternId: number;
  uLocationId: number;
  uwCenterId: number | null;
  schedulingStatusCode: string;
}

// GetFTWorkerView resolves the worker's effective shift pattern per date
// (including future-dated pattern overrides), so the shifts it returns are
// the ones the worker will actually work. Matching raw GetFTShifts instances
// against the worker's current patternId missed those transitions.
const fetchWorkerFTShifts = async (
  worker: { workerId: string; locationId: number },
  start: Date,
  end: Date
): Promise<WorkerFTShift[]> => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_END_POINT}/FTWorkerView/GetFTWorkerView`,
    {
      params: {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        locationId: worker.locationId,
      },
    }
  );
  const workerId = String(worker.workerId).toLowerCase();
  const shifts: WorkerFTShift[] = response.data?.workerFTShifts || [];
  return shifts
    .filter((shift) => String(shift.workerId).toLowerCase() === workerId)
    .sort(
      (a, b) =>
        new Date(a.startDateTime).getTime() -
        new Date(b.startDateTime).getTime()
    );
};

const CalendarLegend = () => {
  const { t } = useTranslation();
  return (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{ width: 16, height: 16, bgcolor: "#2F4D8B", borderRadius: "4px" }}
      />
      <Typography
        variant="caption"
        sx={{ fontFamily: "Roboto", color: "#000000DE" }}
      >
        {t("worker-requests_legend-in-selection")}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          border: "1px solid #FFB74D",
          bgcolor: "#FFF3E0",
          borderRadius: "4px",
        }}
      />
      <Typography
        variant="caption"
        sx={{ fontFamily: "Roboto", color: "#000000DE" }}
      >
        {t("worker-requests_legend-already-off")}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{ width: 16, height: 16, bgcolor: "#F5F5F5", borderRadius: "4px" }}
      />
      <Typography
        variant="caption"
        sx={{ fontFamily: "Roboto", color: "#000000DE" }}
      >
        {t("worker-requests_legend-no-shift")}
      </Typography>
    </Box>
  </Box>
  );
};

const CalendarSkeleton = () => (
  <Box sx={{ width: "100%", py: 2 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pl: 3,
        pr: 1.5,
        mb: 2,
      }}
    >
      <Skeleton variant="text" width={120} height={32} />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="circular" width={32} height={32} />
      </Box>
    </Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: "2px",
        px: 0,
        mb: 0.5,
      }}
    >
      {["M", "T", "W", "T", "F", "S", "S"].map((day, i) => (
        <Box
          key={i}
          sx={{
            height: "46px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: (i === 5 || i === 6) ? "#00000061" : "#00000099",
              fontWeight: 400,
              fontSize: "14px",
              fontFamily: "Roboto",
            }}
          >
            {day}
          </Typography>
        </Box>
      ))}
    </Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: "2px",
      }}
    >
      {Array.from({ length: 31 }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          sx={{ width: "100%", height: "46px", borderRadius: "4px" }}
        />
      ))}
    </Box>
  </Box>
);

interface NewWorkerRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const NewWorkerRequestDialog: React.FC<NewWorkerRequestDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dateLocale = useDateFnsLocale();
  const pickerLocale = useDatePickerLocale();
  const dispatch = useDispatch();
  const { updateAvailabilityStatusStatus } = useWorkerSchedulerData();
  const { allRegularRequests, getFiltersForUser } = useSelector(
    (state: StoreType) => state.workerRequests
  );
  const attendanceManagementEnabledLocations = useSelector(
    (state: StoreType) =>
      state.user.userData.attendanceManagementEnabledLocations
  );

  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null
  );
  const [selectedWorkCenterId, setSelectedWorkCenterId] = useState<
    number | null
  >(null);
  const [hoveredFilter, setHoveredFilter] = useState<string | null>(null);
  const [workers, setWorkers] = useState<any[]>([]);
  const [workerTypes, setWorkerTypes] = useState<any[]>([]);
  const [isLoadingWorkers, setIsLoadingWorkers] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationAnchorEl, setLocationAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [workCenterAnchorEl, setWorkCenterAnchorEl] =
    useState<HTMLElement | null>(null);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [selectedShifts, setSelectedShifts] = useState<any[]>([]);
  const [existingRequestDetail, setExistingRequestDetail] = useState<
    any | null
  >(null);
  const [workerLeaveBalances, setWorkerLeaveBalances] = useState<any[]>([]);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState<number | null>(
    null
  );
  const [selectedLeaveTypeId2, setSelectedLeaveTypeId2] = useState<
    number | null
  >(null);
  const [callOffReason, setCallOffReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPairingAccordionOpen, setIsPairingAccordionOpen] = useState(false);
  const [duration, setDuration] = useState<number | null>(0);
  const [duration2, setDuration2] = useState<number | null>(0);
  const [durationOptions, setDurationOptions] = useState<
    { text: string; timeInMinutes: number }[]
  >([]);
  const [durationOptions2, setDurationOptions2] = useState<
    { text: string; timeInMinutes: number }[]
  >([]);
  const lastSynchronizedHours = useRef<number | null>(null);
  const lastSynchronizedHoursPairing = useRef<number | null>(null);
  const lastFetchedCalendarWorkerId = useRef<string | null>(null);
  const lastFetchedCalendarMonth = useRef<string | null>(null);
  const [workerProfile, setWorkerProfile] = useState<any | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [workerCalendarRequests, setWorkerCalendarRequests] = useState<any[]>(
    []
  );
  const [isLoadingCalendarRequests, setIsLoadingCalendarRequests] =
    useState(false);
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState<Date>(
    new Date()
  );
  const [tempCallOffData, setTempCallOffData] = useState<any>(null);
  const [overlappingRequests, setOverlappingRequests] = useState<any[]>([]);
  const [datesWithNoShifts, setDatesWithNoShifts] = useState<Date[]>([]);
  const [persistentNoShiftDates, setPersistentNoShiftDates] = useState<Date[]>(
    []
  );
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const existingRequestRef = useRef<HTMLDivElement>(null);
  const [resolvedShiftId, setResolvedShiftId] = useState<string | null>(null);
  const [resolvedShift, setResolvedShift] = useState<any | null>(null);
  const [isResolvingShiftId, setIsResolvingShiftId] = useState(false);
  const [workerShifts, setWorkerShifts] = useState<WorkerFTShift[]>([]);

  const resetAllState = useCallback(() => {
    setSelectedWorker(null);
    setStep(1);
    setSelectedDate(null);
    setRangeStart(null);
    setRangeEnd(null);
    setSelectedShifts([]);
    setExistingRequestDetail(null);
    setSelectedLeaveTypeId(null);
    setSelectedLeaveTypeId2(null);
    setCallOffReason("");
    setIsPairingAccordionOpen(false);
    setDuration(0);
    setDuration2(0);
    setWorkerProfile(null);
    setResolvedShiftId(null);
    setResolvedShift(null);
    setTempCallOffData(null);
    setWorkerCalendarRequests([]);
    setOverlappingRequests([]);
    setDatesWithNoShifts([]);
    setPersistentNoShiftDates([]);
    setRangeError(null);
    setApiError(null);
    setWorkerShifts([]);
    lastFetchedCalendarWorkerId.current = null;
    lastFetchedCalendarMonth.current = null;
    setIsLoadingWorkers(true);
    setIsSubmitting(false);
    setCurrentCalendarMonth(new Date());
    setSearchQuery("");
  }, []);

  const handleClose = useCallback(() => {
    resetAllState();
    onClose();
  }, [onClose, resetAllState]);

  const handleBack = useCallback(() => {
    setRangeError(null);
    if (step === 2) {
      setSelectedWorker(null);
      setStep(1);
      setWorkerProfile(null);
      setCurrentCalendarMonth(new Date());
    } else if (step === 3) {
      setSelectedDate(null);
      setRangeStart(null);
      setRangeEnd(null);
      setSelectedShifts([]);
      setSelectedLeaveTypeId(null);
      setSelectedLeaveTypeId2(null);
      setDuration(0);
      setDuration2(0);
      setIsPairingAccordionOpen(false);
      setStep(2);
    }
  }, [step]);

  const getShiftHours = useCallback(
    (date: Date) => {
      if (!workerProfile?.shiftPattern?.schedule) return 8;
      const pattern = workerProfile.shiftPattern;
      const startDate = new Date(pattern.startDate);
      startDate.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);

      const diffTime = checkDate.getTime() - startDate.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      const patternDays = Number(pattern.numberOfDays);
      if (patternDays === 0) return 8;
      const dayInPattern = (diffDays % patternDays) + 1;

      const daySchedule = pattern.schedule.find(
        (s: any) => s.dayNo === dayInPattern
      );
      if (daySchedule?.shiftType) {
        const start = new Date(`2000-01-01T${daySchedule.shiftType.startTime}`);
        const end = new Date(`2000-01-01T${daySchedule.shiftType.endTime}`);
        if (end < start) end.setDate(end.getDate() + 1);
        return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      }
      return 8;
    },
    [workerProfile]
  );

  const totalSelectedHours = useMemo(() => {
    if (selectedDate) return getShiftHours(selectedDate);
    return selectedShifts.reduce((acc, shift) => {
      const start = new Date(shift.startDateTime);
      const end = new Date(shift.endDateTime);
      return acc + (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    }, 0);
  }, [selectedDate, selectedShifts, getShiftHours]);

  const fetchWorkerTypes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Types`
      );
      setWorkerTypes(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error("Failed to fetch worker types", error);
      return [];
    }
  }, []);

  const fetchWorkers = useCallback(async () => {
    setIsLoadingWorkers(true);
    try {
      const params = new URLSearchParams();
      locations.forEach((l) => params.append("locationIds", l.id));
      params.append("ftOnly", "true");

      const response = await axios.get(
        `${process.env.REACT_APP_API_END_POINT}/companies/workers/list`,
        {
          params,
        }
      );

      const sortedWorkers = (response.data || []).sort((a: any, b: any) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setWorkers(sortedWorkers);
    } catch (error) {
      console.error("Failed to fetch workers", error);
    } finally {
      setIsLoadingWorkers(false);
    }
  }, [locations]);

  useEffect(() => {
    if (open && getFiltersForUser?.data?.locations) {
      let filteredLocations = getFiltersForUser.data.locations;
      if (
        attendanceManagementEnabledLocations &&
        attendanceManagementEnabledLocations.length > 0
      ) {
        filteredLocations = filteredLocations.filter((location: any) =>
          attendanceManagementEnabledLocations.includes(location.id)
        );
      }
      setLocations(filteredLocations);
      if (filteredLocations.length === 0) {
        setIsLoadingWorkers(false);
      }
    } else if (open && getFiltersForUser?.status !== "pending") {
      if (getFiltersForUser?.status === "idle") {
        dispatch(fetchFiltersForUser() as any);
      } else {
        setIsLoadingWorkers(false);
      }
    }
  }, [open, getFiltersForUser, dispatch, attendanceManagementEnabledLocations]);

  useEffect(() => {
    if (open && locations.length > 0) {
      fetchWorkers();
    }
  }, [open, locations, fetchWorkers]);

  useEffect(() => {
    if (!open) {
      resetAllState();
      setWorkers([]);
      setLocations([]);
      setSelectedLocationId(null);
      setSelectedWorkCenterId(null);
    }
  }, [open, resetAllState]);

  useEffect(() => {
    if (updateAvailabilityStatusStatus === "fulfilled") {
      dispatch(resetUpdateWorkerAvailabilityStatus());
      onSuccess?.();
      handleClose();
    }
  }, [dispatch, handleClose, onSuccess, updateAvailabilityStatusStatus]);

  const workCenters = getFiltersForUser?.data?.workCenters || [];
  const filterRelationships =
    getFiltersForUser?.data?.filterRelationships || [];

  const displayWorkers = React.useMemo(() => {
    return workers.filter((worker: any) => {
      const matchesLocation =
        !selectedLocationId ||
        Number(worker.locationId) === Number(selectedLocationId);
      const matchesWorkCenter =
        !selectedWorkCenterId ||
        Number(worker.workCenterId) === Number(selectedWorkCenterId);

      const search = searchQuery.toLowerCase();
      const matchesSearch =
        !search ||
        `${worker.firstName} ${worker.lastName}`
          .toLowerCase()
          .includes(search) ||
        (worker.badgeNumber &&
          worker.badgeNumber.toString().toLowerCase().includes(search)) ||
        (worker.companyEmployeeId &&
          worker.companyEmployeeId.toString().toLowerCase().includes(search));

      return matchesLocation && matchesWorkCenter && matchesSearch;
    });
  }, [workers, selectedLocationId, selectedWorkCenterId, searchQuery]);

  const filteredWorkCenters = workCenters.filter((wc: any) => {
    if (!selectedLocationId) return false;
    return (
      Number(wc.locationId) === Number(selectedLocationId) ||
      filterRelationships.some(
        (rel: any) =>
          rel.parentId === selectedLocationId &&
          rel.childId === wc.id &&
          rel.parentFilterName === "location" &&
          rel.childFilterName === "workCenter"
      )
    );
  });

  useEffect(() => {
    if (open && selectedWorker) {
      dispatch(fetchWorkerCallOffs(selectedWorker.workerId) as any);
    }
  }, [open, selectedWorker, dispatch]);

  const leaveBalances = useSelector(
    (state: StoreType) => state.workerScheduler.fetchWorkerCallOffs.data || []
  );

  useEffect(() => {
    setWorkerLeaveBalances(leaveBalances);
    if (leaveBalances.length > 0 && !selectedLeaveTypeId) {
      const vacationType = leaveBalances.find((b: any) =>
        b.name.toLowerCase().includes("vacation")
      );
      setSelectedLeaveTypeId(vacationType?.id || leaveBalances[0].id);
    }
  }, [leaveBalances, selectedLeaveTypeId]);

  useEffect(() => {
    if (selectedShifts.length > 0 || selectedDate) {
      const totalMinutes = totalSelectedHours * 60;
      const isPairingActive = isPairingAccordionOpen && selectedLeaveTypeId2;

      if (
        !isPairingActive &&
        (duration === 0 ||
          duration === null ||
          lastSynchronizedHours.current !== totalSelectedHours)
      ) {
        if (duration !== totalMinutes) {
          setDuration(totalMinutes);
        }
        lastSynchronizedHours.current = totalSelectedHours;
      }

      const leaveType = workerLeaveBalances.find(
        (b) => b.id === selectedLeaveTypeId
      );
      const increment = leaveType?.incrementHours || 1;
      const options = generateDurationOptionsByMinutes(
        totalSelectedHours * 60,
        increment.toString()
      );
      setDurationOptions(options);
    } else {
      setDuration(0);
      setDurationOptions([]);
      lastSynchronizedHours.current = null;
    }
  }, [
    selectedShifts,
    selectedDate,
    totalSelectedHours,
    selectedLeaveTypeId,
    workerLeaveBalances,
    duration,
    isPairingAccordionOpen,
    selectedLeaveTypeId2,
  ]);

  useEffect(() => {
    if (isPairingAccordionOpen && selectedLeaveTypeId2) {
      const leaveType2 = workerLeaveBalances.find(
        (b) => b.id === selectedLeaveTypeId2
      );
      const increment2 = leaveType2?.incrementHours || 1;

      if (
        duration2 === 0 ||
        duration2 === null ||
        lastSynchronizedHoursPairing.current !== totalSelectedHours
      ) {
        const totalMinutes = totalSelectedHours * 60;

        if (duration2 !== 0) {
          setDuration2(0);
        }
        if (duration !== totalMinutes) {
          setDuration(totalMinutes);
        }
        lastSynchronizedHoursPairing.current = totalSelectedHours;
      }

      const options2 = generateDurationOptionsByMinutes(
        totalSelectedHours * 60,
        increment2.toString()
      );
      setDurationOptions2(options2);
    } else {
      setDuration2(0);
      setDurationOptions2([]);
      lastSynchronizedHoursPairing.current = null;
    }
  }, [
    isPairingAccordionOpen,
    selectedLeaveTypeId,
    selectedLeaveTypeId2,
    totalSelectedHours,
    workerLeaveBalances,
    duration2,
    duration,
  ]);

  const showPairingAccordion = useMemo(() => {
    const selectedLeaveType = workerLeaveBalances.find(
      (b) => b.id === selectedLeaveTypeId
    );
    if (!selectedLeaveType || !selectedLeaveType.pairingEnabled) {
      return false;
    }
    const otherPairingEnabledTypes = workerLeaveBalances.filter(
      (type) => type.pairingEnabled && type.id !== selectedLeaveType.id
    );
    return (
      otherPairingEnabledTypes.length > 0 &&
      ((durationOptions?.length > 1 && !selectedLeaveTypeId2) ||
        (durationOptions?.length > 0 && selectedLeaveTypeId2))
    );
  }, [
    durationOptions?.length,
    selectedLeaveTypeId,
    workerLeaveBalances,
    selectedLeaveTypeId2,
  ]);

  const fetchWorkerCalendarData = useCallback(async () => {
    if (!selectedWorker || step !== 2) return;

    const monthKey = format(currentCalendarMonth, "yyyy-MM");
    if (
      lastFetchedCalendarWorkerId.current === selectedWorker.workerId &&
      lastFetchedCalendarMonth.current === monthKey
    ) {
      return;
    }

    setIsLoadingCalendarRequests(true);
    try {
      const now = new Date();
      const monthStart = new Date(
        currentCalendarMonth.getFullYear(),
        currentCalendarMonth.getMonth(),
        1
      );
      const monthEnd = new Date(
        currentCalendarMonth.getFullYear(),
        currentCalendarMonth.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );

      const actualMonthStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        1
      );
      const actualMonthEnd = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );

      const fetchStart = monthStart < actualMonthStart ? monthStart : actualMonthStart;
      const fetchEnd = monthEnd > actualMonthEnd ? monthEnd : actualMonthEnd;

      const [requestsResponse, shifts] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_END_POINT}/workers/requestsForUser`,
          {
            params: {
              workerId: selectedWorker.workerId,
              startDate: fetchStart.toISOString(),
              endDate: fetchEnd.toISOString(),
            },
          }
        ),
        fetchWorkerFTShifts(selectedWorker, fetchStart, fetchEnd),
      ]);

      setWorkerCalendarRequests(requestsResponse.data || []);
      setWorkerShifts(shifts);
      lastFetchedCalendarWorkerId.current = selectedWorker.workerId;
      lastFetchedCalendarMonth.current = monthKey;
    } catch (error) {
      console.error("Failed to fetch worker calendar data", error);
    } finally {
      setIsLoadingCalendarRequests(false);
    }
  }, [selectedWorker, step, currentCalendarMonth]);

  useEffect(() => {
    if (open && selectedWorker && step === 2) {
      fetchWorkerCalendarData();
    }
  }, [
    open,
    selectedWorker,
    step,
    currentCalendarMonth,
    fetchWorkerCalendarData,
  ]);

  useEffect(() => {
    if (existingRequestDetail && existingRequestRef.current) {
      existingRequestRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [existingRequestDetail]);

  const handleLeaveTypeChange = (e: any) => {
    const newTypeId = Number(e.target.value);
    setSelectedLeaveTypeId(newTypeId);
    setIsPairingAccordionOpen(false);
    setSelectedLeaveTypeId2(null);
    setDuration2(0);
  };

  const handleWorkerSelect = (_event: any, newValue: any) => {
    setRangeError(null);
    setSelectedWorker(newValue);
    setSelectedDate(null);
    setRangeStart(null);
    setRangeEnd(null);
    setSelectedShifts([]);
    setExistingRequestDetail(null);
    setResolvedShiftId(null);
    setResolvedShift(null);
    setWorkerCalendarRequests([]);
    setWorkerShifts([]);
    setPersistentNoShiftDates([]);
    setCurrentCalendarMonth(new Date());
    lastFetchedCalendarWorkerId.current = null;
    lastFetchedCalendarMonth.current = null;
    if (newValue) {
      fetchWorkerProfileData(newValue.workerId);
    } else {
      setWorkerProfile(null);
    }
  };

  const fetchWorkerProfileData = async (workerId: string) => {
    setIsLoadingProfile(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_END_POINT}/companies/workerprofile/${workerId}`
      );
      setWorkerProfile(response.data);
    } catch (error) {
      console.error("Failed to fetch worker profile", error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleNext = async () => {
    if (step === 1 && selectedWorker) {
      setStep(2);
    } else if (step === 2 && (selectedDate || selectedShifts.length > 0)) {
      setStep(3);
    }
  };

  const handleToggleStep1Location = (event: React.MouseEvent<HTMLElement>) => {
    setLocationAnchorEl(event.currentTarget);
  };

  const handleToggleStep1WorkCenter = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setWorkCenterAnchorEl(event.currentTarget);
  };

  const handleCloseStep1Filters = () => {
    setLocationAnchorEl(null);
    setWorkCenterAnchorEl(null);
  };

  const handleMouseEnterFilter = (filterName: string) => {
    setHoveredFilter(filterName);
  };

  const handleMouseLeaveFilter = () => {
    setHoveredFilter(null);
  };

  const fetchShiftsForRange = async (start: Date, end: Date) => {
    if (!selectedWorker) return [];

    const startDate = start < end ? start : end;
    const endDate = start < end ? end : start;

    // Try to use already fetched shifts if the range is within the cached months
    const now = new Date();
    const monthStart = new Date(
      currentCalendarMonth.getFullYear(),
      currentCalendarMonth.getMonth(),
      1
    );
    const monthEnd = new Date(
      currentCalendarMonth.getFullYear(),
      currentCalendarMonth.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );

    const actualMonthStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    );
    const actualMonthEnd = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );

    const cacheStart = monthStart < actualMonthStart ? monthStart : actualMonthStart;
    const cacheEnd = monthEnd > actualMonthEnd ? monthEnd : actualMonthEnd;

    if (
      startDate >= cacheStart &&
      endDate <= cacheEnd &&
      workerShifts &&
      workerShifts.length > 0
    ) {
      return workerShifts.filter((shift) => {
        const shiftDate = new Date(shift.startDateTime);
        return (
          shiftDate >= startOfDay(startDate) &&
          shiftDate <= new Date(new Date(endDate).setHours(23, 59, 59, 999))
        );
      });
    }

    setIsResolvingShiftId(true);
    try {
      const fetchStart = startOfDay(startDate);
      const fetchEnd = new Date(new Date(endDate).setHours(23, 59, 59, 999));
      return await fetchWorkerFTShifts(selectedWorker, fetchStart, fetchEnd);
    } catch (error) {
      console.error("Failed to fetch shifts for range", error);
      return [];
    } finally {
      setIsResolvingShiftId(false);
    }
  };

  const clearRangeStart = () => {
    setRangeError(null);
    if (rangeStart && rangeEnd && isSameDay(rangeStart, rangeEnd)) {
      setRangeStart(null);
      setRangeEnd(null);
      setDatesWithNoShifts([]);
      setSelectedDate(null);
      setSelectedShifts([]);
      setOverlappingRequests([]);
      return;
    }
    setRangeStart(null);
    setDatesWithNoShifts([]);
    setSelectedDate(rangeEnd);
    if (!rangeEnd) {
      setSelectedShifts([]);
      setOverlappingRequests([]);
    } else {
      fetchShiftId(rangeEnd).then((shift) => {
        setSelectedShifts(shift ? [shift] : []);
      });
      setOverlappingRequests([]);
    }
  };

  const clearRangeEnd = () => {
    setRangeError(null);
    if (rangeStart && rangeEnd && isSameDay(rangeStart, rangeEnd)) {
      setRangeStart(null);
      setRangeEnd(null);
      setDatesWithNoShifts([]);
      setSelectedDate(null);
      setSelectedShifts([]);
      setOverlappingRequests([]);
      return;
    }
    setRangeEnd(null);
    setDatesWithNoShifts([]);
    setSelectedDate(rangeStart);
    if (!rangeStart) {
      setSelectedShifts([]);
      setOverlappingRequests([]);
    } else {
      fetchShiftId(rangeStart).then((shift) => {
        setSelectedShifts(shift ? [shift] : []);
      });
      setOverlappingRequests([]);
    }
  };

  const handleDateClick = async (date: Date) => {
    setRangeError(null);
    const existingReq = getExistingRequest(date);

    if (shouldDisableDate(date)) {
      if (existingReq) {
        setExistingRequestDetail(existingReq);
      }
      return;
    }

    if (existingReq) {
      setExistingRequestDetail(existingReq);
      // If we are currently selecting a range, don't allow existing request as end date
      if (rangeStart && !isSameDay(rangeStart, rangeEnd || rangeStart)) {
        return;
      }
      // If nothing selected or only start selected (and it's the same as date),
      // we just show info and return to prevent selecting it.
      return;
    }

    setExistingRequestDetail(null);
    setOverlappingRequests([]);
    setDatesWithNoShifts([]);

    if (!rangeStart && !rangeEnd) {
      // Nothing selected yet
      setRangeStart(date);
      setRangeEnd(date);
      setSelectedDate(date);
      const shift = await fetchShiftId(date);
      setSelectedShifts(shift ? [shift] : []);
      if (!shift) {
        setPersistentNoShiftDates((prev) => {
          if (prev.some((d) => isSameDay(d, date))) return prev;
          return [...prev, date];
        });
      }
    } else if (
      (rangeStart && !rangeEnd) ||
      (!rangeStart && rangeEnd) ||
      (rangeStart && rangeEnd && isSameDay(rangeStart, rangeEnd))
    ) {
      // One date selected, selecting second date to form a range
      const referenceDate = rangeStart || rangeEnd;
      const start = date < referenceDate! ? date : referenceDate!;
      const end = date < referenceDate! ? referenceDate! : date;

      if (isSameDay(start, end)) return;

      if (differenceInDays(end, start) + 1 > 31) {
        setRangeError(t("worker-requests_max-selection-range"));
        return;
      }

      setRangeStart(start);
      setRangeEnd(end);
      setSelectedDate(null);

      const shifts = await fetchShiftsForRange(start, end);
      const overlapsMap = new Map();
      const current = new Date(start);
      const endBoundary = startOfDay(end);

      while (startOfDay(current) <= endBoundary) {
        const existing = getExistingRequest(current);
        if (existing && !overlapsMap.has(existing.requestId)) {
          overlapsMap.set(existing.requestId, {
            ...existing,
            overlapDate: new Date(current),
          });
        }
        current.setDate(current.getDate() + 1);
      }

      const availableShifts = shifts.filter((shift: any) => {
        const existing = getExistingRequest(new Date(shift.startDateTime));
        return !existing;
      });

      setSelectedShifts(availableShifts);
      setOverlappingRequests(Array.from(overlapsMap.values()));

      // Identify dates without shifts in the range
      const noShiftDates: Date[] = [];
      const checkDate = new Date(start);
      while (startOfDay(checkDate) <= endBoundary) {
        const hasShift = shifts.some((s: any) =>
          isSameDay(new Date(s.startDateTime), checkDate)
        );
        if (!hasShift) {
          noShiftDates.push(new Date(checkDate));
        }
        checkDate.setDate(checkDate.getDate() + 1);
      }
      setDatesWithNoShifts(noShiftDates);
      setPersistentNoShiftDates((prev) => {
        const newDates = [...prev];
        noShiftDates.forEach((d) => {
          if (!newDates.some((pd) => isSameDay(pd, d))) {
            newDates.push(d);
          }
        });
        return newDates;
      });
    } else if (rangeStart && rangeEnd) {
      // Both selected (multi-day range), reset to single date
      setRangeStart(date);
      setRangeEnd(date);
      setSelectedDate(date);
      const shift = await fetchShiftId(date);
      setSelectedShifts(shift ? [shift] : []);
      if (!shift) {
        setPersistentNoShiftDates((prev) => {
          if (prev.some((d) => isSameDay(d, date))) return prev;
          return [...prev, date];
        });
      }
    }
  };

  const handleRemoveShiftFromBatch = (index: number) => {
    const removedShift = selectedShifts[index];
    const newShifts = selectedShifts.filter((_, i) => i !== index);
    setSelectedShifts(newShifts);

    // Update overlapping requests
    if (removedShift) {
      setOverlappingRequests((prev) =>
        prev.filter(
          (req) =>
            !isSameDay(
              new Date(req.overlapDate),
              new Date(removedShift.startDateTime)
            )
        )
      );
    }

    if (newShifts.length === 1) {
      const remainingShift = newShifts[0];
      const date = new Date(remainingShift.startDateTime);
      setSelectedDate(date);
      setResolvedShift(remainingShift);
      setResolvedShiftId(remainingShift.shiftId);
      setRangeStart(date);
      setRangeEnd(date);
    } else if (newShifts.length === 0) {
      setSelectedDate(null);
      setResolvedShift(null);
      setResolvedShiftId(null);
      setRangeStart(null);
      setRangeEnd(null);
      setStep(2); // Go back to calendar if no shifts left
    } else {
      // Still multiple shifts, update range boundaries to reflect remaining shifts
      const startDates = newShifts.map((s) =>
        new Date(s.startDateTime).getTime()
      );
      const minDate = new Date(Math.min(...startDates));
      const maxDate = new Date(Math.max(...startDates));
      setRangeStart(minDate);
      setRangeEnd(maxDate);
      setSelectedDate(null);
    }
  };

  const getExistingRequest = (date: Date) => {
    if (!selectedWorker) return null;
    const dateStr = format(date, "yyyy-MM-dd");

    // Use calendar requests if available, fallback to allRegularRequests
    const source =
      workerCalendarRequests.length > 0
        ? workerCalendarRequests
        : allRegularRequests || [];

    const found = source.find((request: any) => {
      // Check if the request is for the same worker
      // Standardize to lowercase for comparison to be safe
      const reqWorkerId = String(request.workerId).toLowerCase();
      const selWorkerId = String(selectedWorker.workerId).toLowerCase();

      if (reqWorkerId !== selWorkerId || request.status?.code === "w") {
        return false;
      }

      // Check if the request is for the same date
      const requestDateStr = request.startDate
        ? format(new Date(request.startDate), "yyyy-MM-dd")
        : null;
      const ftShiftDateStr = request.ftShift?.startDateTime
        ? format(new Date(request.ftShift.startDateTime), "yyyy-MM-dd")
        : null;

      return (
        requestDateStr === dateStr ||
        ftShiftDateStr === dateStr
      );
    });

    return found;
  };

  const fetchShiftId = async (dateOverride?: Date | null) => {
    const dateToUse = dateOverride !== undefined ? dateOverride : selectedDate;
    if (!selectedWorker || !dateToUse) return null;

    // Check if we already have the shift in our month-fetch
    if (workerShifts && workerShifts.length > 0) {
      const existingShift = workerShifts.find((shift: any) =>
        isSameDay(new Date(shift.startDateTime), dateToUse)
      );
      if (existingShift) {
        setResolvedShiftId(existingShift.shiftId);
        setResolvedShift(existingShift);
        return existingShift;
      }
    }

    setIsResolvingShiftId(true);
    setResolvedShiftId(null);
    setResolvedShift(null);
    try {
      const startDate = new Date(dateToUse);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dateToUse);
      endDate.setHours(23, 59, 59, 999);

      const shifts = await fetchWorkerFTShifts(
        selectedWorker,
        startDate,
        endDate
      );
      const matchedShift = shifts.find((shift) =>
        isSameDay(new Date(shift.startDateTime), dateToUse)
      );

      if (matchedShift) {
        setResolvedShiftId(matchedShift.shiftId);
        setResolvedShift(matchedShift);
        return matchedShift;
      }

      setResolvedShiftId(null);
      setResolvedShift(null);
      return null;
    } catch (error) {
      console.error("Failed to fetch shift ID", error);
      setResolvedShiftId(null);
      setResolvedShift(null);
      return null;
    } finally {
      setIsResolvingShiftId(false);
    }
  };

  const isShiftDate = useCallback(
    (date: Date) =>
      workerShifts.some((shift) =>
        isSameDay(new Date(shift.startDateTime), date)
      ),
    [workerShifts]
  );
  const handleBatchConfirm = async () => {
    if (!selectedWorker || selectedShifts.length === 0 || !selectedLeaveTypeId)
      return;

    setIsSubmitting(true);
    try {
      const payload = {
        workerId: selectedWorker.workerId,
        requestTypeId: 2, // Assuming 2 is the standard Time Off request type
        callOffReason: callOffReason,
        shifts: selectedShifts.map((shift) => {
          const shiftStart = new Date(shift.startDateTime);
          const shiftEnd = new Date(shift.endDateTime);
          const totalShiftHours =
            (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60);

          const leaveTypes = [];

          const primaryLeaveType2 = workerLeaveBalances.find(
            (b) => b.id === selectedLeaveTypeId
          );

          const isSecondaryVisible =
            isPairingAccordionOpen || !!selectedLeaveTypeId2;

          const requestedPrimaryHours =
            duration !== null
              ? (duration / 60) * (totalShiftHours / totalSelectedHours)
              : totalShiftHours;
          const requestedSecondaryHours =
            duration2 !== null
              ? (duration2 / 60) * (totalShiftHours / totalSelectedHours)
              : 0;

          if (isSecondaryVisible && selectedLeaveTypeId2) {
            // If user explicitly provided durations, we apply them proportionally to each shift.
            leaveTypes.push({
              leaveTypeId: selectedLeaveTypeId,
              requestedHours: requestedPrimaryHours,
            });
            if (requestedSecondaryHours > 0) {
              leaveTypes.push({
                leaveTypeId: selectedLeaveTypeId2,
                requestedHours: requestedSecondaryHours,
              });
            }
          } else {
            leaveTypes.push({
              leaveTypeId: selectedLeaveTypeId,
              requestedHours: requestedPrimaryHours,
            });
          }

          return {
            ftShiftInstId: shift.shiftId,
            startDate: shift.startDateTime,
            endDate: shift.endDateTime,
            leaveTypes,
          };
        }),
      };

      await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/workers/requestsBatch`,
        payload
      );
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error("Batch request failed", error);
      setApiError(
        error?.response?.data?.message || t("common_error-occurred")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const shouldDisableDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only future dates (including today if it's a shift date)
    if (date < today) {
      return true;
    }

    if (!isShiftDate(date)) {
      return true;
    }

    // If there is already a request for that worker for that date and same shift then do not allow selection
    if (selectedWorker) {
      if (persistentNoShiftDates.some((d) => isSameDay(d, date))) {
        return true;
      }
    }

    return false;
  };

  const getShiftTiming = useCallback(() => {
    if (resolvedShift) {
      return {
        start: new Date(resolvedShift.startDateTime).toISOString(),
        end: new Date(resolvedShift.endDateTime).toISOString(),
      };
    }

    if (
      !selectedDate ||
      !workerProfile?.shiftPattern?.schedule ||
      !workerProfile?.shiftPattern?.startDate
    ) {
      const start = new Date(selectedDate || new Date());
      start.setHours(0, 0, 0, 0);
      const end = new Date(selectedDate || new Date());
      end.setHours(23, 59, 59, 999);
      return {
        start: start.toISOString(),
        end: end.toISOString(),
      };
    }

    const pattern = workerProfile.shiftPattern;
    const startDate = new Date(pattern.startDate);
    startDate.setHours(0, 0, 0, 0);
    const checkDate = new Date(selectedDate);
    checkDate.setHours(0, 0, 0, 0);

    const diffTime = checkDate.getTime() - startDate.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    const patternDays = Number(pattern.numberOfDays);
    const dayInPattern = (diffDays % patternDays) + 1;

    const daySchedule = pattern.schedule.find(
      (s: any) => s.dayNo === dayInPattern
    );

    if (daySchedule?.shiftType) {
      const [startHours, startMinutes] = daySchedule.shiftType.startTime
        .split(":")
        .map(Number);
      const [endHours, endMinutes] = daySchedule.shiftType.endTime
        .split(":")
        .map(Number);

      const shiftStart = new Date(checkDate);
      shiftStart.setHours(startHours, startMinutes, 0, 0);

      const shiftEnd = new Date(checkDate);
      shiftEnd.setHours(endHours, endMinutes, 0, 0);

      // Handle overnight shifts
      if (shiftEnd < shiftStart) {
        shiftEnd.setDate(shiftEnd.getDate() + 1);
      }

      return {
        start: shiftStart.toISOString(),
        end: shiftEnd.toISOString(),
      };
    }

    const start = new Date(checkDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(checkDate);
    end.setHours(23, 59, 59, 999);

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  }, [resolvedShift, selectedDate, workerProfile]);

  const handleConfirm = async (callOffData: any) => {
    const data =
      callOffData && callOffData.ftRequestTypeId ? callOffData : tempCallOffData;

    if (!data || !data.ftRequestTypeId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        updateWorkerAvailabilityStatus({
          workerId: selectedWorker.workerId,
          toStatusCode: "COF",
          ftShiftId: resolvedShiftId,
          callOffParams: {
            requestTypeId: data.ftRequestTypeId,
            leaveTypes: data.ftLeaveTypes,
            supervisorComment: data.ftSupervisorComment,
            startDate: data.ftStartDateTime,
            endDate: data.ftEndDateTime,
          },
        }) as any
      );
    } catch (error) {
      console.error("Failed to update worker availability status", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDay = (
    pickersDayProps: PickersDayProps<Date>
  ) => {
    const { day: date } = pickersDayProps;
    const isSelected =
      (rangeStart &&
        rangeEnd &&
        !isSameDay(rangeStart, rangeEnd) &&
        isWithinInterval(startOfDay(date), {
          start: startOfDay(rangeStart < rangeEnd ? rangeStart : rangeEnd),
          end: startOfDay(rangeStart < rangeEnd ? rangeEnd : rangeStart),
        })) ||
      (rangeStart && isSameDay(date, rangeStart)) ||
      (rangeEnd && isSameDay(date, rangeEnd)) ||
      (selectedDate && isSameDay(date, selectedDate));

    const isStart = rangeStart && isSameDay(date, rangeStart);
    const isEnd = rangeEnd && isSameDay(date, rangeEnd);
    const isRange = rangeStart && rangeEnd && !isSameDay(rangeStart, rangeEnd);

    const isToday = isSameDay(date, new Date());
    const hasExisting = getExistingRequest(date);
    const isNoShiftInRange = datesWithNoShifts.some((d) => isSameDay(d, date));
    const isPersistentNoShift = persistentNoShiftDates.some((d) =>
      isSameDay(d, date)
    );
    const isNoShift =
      !isShiftDate(date) || isNoShiftInRange || isPersistentNoShift;
    const isPast = startOfDay(date) < startOfDay(new Date());

    let sx: any = {
      width: "100%",
      height: "48px",
      fontSize: "1rem",
      margin: "0",
    };

    if (hasExisting) {
      sx = {
        ...sx,
        bgcolor: "#FFF3E0 !important",
        border: "1px solid #FFB74D",
        color: "#E65100 !important",
        fontWeight: "bold",
        borderRadius: "4px",
      };
      if (isSelected) {
        // No selection border for existing requests
      }
    } else if (isSelected) {
      const isIntermediate = isRange && !isStart && !isEnd;
      sx = {
        ...sx,
        bgcolor: isIntermediate ? "#E8F0FE !important" : "#2F4D8B !important",
        color: isIntermediate ? "#2F4D8B !important" : "white !important",
        borderRadius: "4px",
      };

      if (isNoShift || (isPast && !hasExisting)) {
        sx = {
          ...sx,
          bgcolor: "#F5F5F5 !important",
          color: "rgba(0, 0, 0, 0.38) !important",
          // No selection border for disabled dates
        };
      }
    } else if (isNoShift || isPast) {
      sx = {
        ...sx,
        bgcolor: "#F5F5F5",
        color: "rgba(0, 0, 0, 0.38)",
        borderRadius: "4px",
      };
    } else {
      sx = {
        ...sx,
        borderRadius: "4px",
      }
    }

    return (
      <PickersDay
        {...pickersDayProps}
        sx={sx}
        disabled={
          ((isNoShift || isPast) && !hasExisting) ||
          isNoShiftInRange
        }
        onClick={() => handleDateClick(date)}
      />
    );
  };

  const callOffSectionCurrentAssignment = useMemo(() => {
    if (!selectedWorker) return null;
    return {
      job:
        workerProfile?.primaryJob?.name ||
        selectedWorker.primaryJobName ||
        selectedWorker.jobWorkcenters?.find(
          (jw: any) => jw.jobId === selectedWorker.primaryJobId
        )?.jobName ||
        (isLoadingProfile ? "" : t("common_not-applicable")),
      workcenter:
        workerProfile?.workCenter?.name ||
        selectedWorker.workCenterName ||
        selectedWorker.jobWorkcenters?.find(
          (jw: any) => jw.workCenterId === selectedWorker.workCenterId
        )?.workCenterName ||
        (isLoadingProfile ? "" : t("common_not-applicable")),
      badgeNumber:
        workerProfile?.badgeNumber ||
        workerProfile?.companyEmployeeId ||
        selectedWorker.badgeNumber ||
        selectedWorker.companyEmployeeId ||
        (isLoadingProfile ? "" : t("common_not-applicable")),
      location:
        workerProfile?.location?.name ||
        locations.find((l) => l.id === selectedLocationId)?.name ||
        (isLoadingProfile ? "" : t("common_not-applicable")),
    };
  }, [
    workerProfile,
    selectedWorker,
    isLoadingProfile,
    locations,
    selectedLocationId,
    t,
  ]);

  // Worker view rows carry the pattern effective on each shift date, so
  // names resolve against the profile's current pattern and its pending
  // change instead of the static profile snapshot alone.
  const getPatternName = useCallback(
    (patternId?: number | null) => {
      if (patternId === undefined || patternId === null) return "";
      const id = Number(patternId);
      const currentPattern = workerProfile?.shiftPattern;
      if (currentPattern && Number(currentPattern.id) === id) {
        return currentPattern.name || "";
      }
      const pendingChange = workerProfile?.pendingPatternChange;
      if (pendingChange && Number(pendingChange.shiftPatternId) === id) {
        return pendingChange.shiftPatternName || "";
      }
      return "";
    },
    [workerProfile]
  );

  const selectedShiftPatternNames = useMemo(() => {
    const names = selectedShifts.map((shift) =>
      getPatternName(shift.uPatternId)
    );
    return names.filter(
      (name, index) => name && names.indexOf(name) === index
    );
  }, [selectedShifts, getPatternName]);

  const hasMultiplePatternsSelected = selectedShiftPatternNames.length > 1;

  const callOffSectionShiftDetails = useMemo(() => {
    if (!selectedWorker) return null;
    const timing = getShiftTiming();
    return {
      shiftPatternName:
        getPatternName(resolvedShift?.uPatternId) ||
        workerProfile?.shiftPattern?.name ||
        selectedWorker.shiftPatternName ||
        (isLoadingProfile ? "" : t("common_not-applicable")),
      startDateTime: timing.start,
      endDateTime: timing.end,
    };
  }, [
    workerProfile,
    selectedWorker,
    isLoadingProfile,
    getShiftTiming,
    getPatternName,
    resolvedShift,
    t,
  ]);

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: "600px",
          },
        }}
      >
        {step !== 3 && (
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                padding: "29px 29px 0px",
              }}
            >
              {step === 1
                ? t("worker-requests_mark-time-off-select-worker")
                : step === 2
                ? t("worker-requests_mark-time-off-select-date")
                : ""}
            </Typography>
          </DialogTitle>
        )}
        <DialogContent sx={{ p: 0, overflowY: "auto" }}>
          {step === 1 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "auto",
                  minHeight: "302px",
                  justifyContent: "center",
                  alignItems: "stretch",
                  width: "100%",
                  padding: "0px 45px",
                  gap: 3,
                }}
              >
                {isLoadingWorkers ? (
                  <Box
                    sx={{
                      height: "476px",
                      p: "0px",
                      gap: 3,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      height={56}
                      sx={{ borderRadius: 1 }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={40}
                      sx={{ borderRadius: 1 }}
                    />
                    <Box>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton
                          key={i}
                          variant="rectangular"
                          height={60}
                          sx={{ borderRadius: 1, mb: 1 }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "476px",
                      width: "100%",
                    }}
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={t("common_search")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchQuery("")}
                              edge="end"
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{ position: "relative", display: "inline-flex" }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleToggleStep1Location}
                          sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            borderRadius: "18px",
                            borderColor: selectedLocationId
                              ? colors.title
                              : "rgba(0,0,0,0.08)",
                            color: selectedLocationId
                              ? colors.title
                              : "rgba(0,0,0,0.6)",
                            fontWeight: selectedLocationId ? 500 : 400,
                            backgroundColor: selectedLocationId
                              ? "rgba(0,0,0,0.04)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: selectedLocationId
                                ? "rgba(0,0,0,0.08)"
                                : "rgba(0,0,0,0.04)",
                              borderColor: selectedLocationId
                                ? colors.title
                                : "rgba(0,0,0,0.08)",
                            },
                          }}
                        >
                          {t("worker-requests_location-label", {
                            name:
                              locations.find(
                                (l) => l.id === selectedLocationId
                              )?.name || t("worker-requests_all"),
                          })}
                        </Button>
                        <Popover
                          open={Boolean(locationAnchorEl)}
                          anchorEl={locationAnchorEl}
                          onClose={handleCloseStep1Filters}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                        >
                          <List sx={{ minWidth: 200, maxHeight: 300 }}>
                            <ListItemButton
                              selected={selectedLocationId === null}
                              onClick={() => {
                                setSelectedLocationId(null);
                                setSelectedWorkCenterId(null);
                                handleCloseStep1Filters();
                              }}
                            >
                              <ListItemText
                                primary={t("worker-requests_all-locations")}
                              />
                            </ListItemButton>
                            {locations.map((loc) => (
                              <ListItemButton
                                key={loc.id}
                                selected={selectedLocationId === loc.id}
                                onClick={() => {
                                  setSelectedLocationId(loc.id);
                                  setSelectedWorkCenterId(null);
                                  handleCloseStep1Filters();
                                }}
                              >
                                <ListItemText primary={loc.name} />
                              </ListItemButton>
                            ))}
                          </List>
                        </Popover>
                      </Box>

                      <Box
                        sx={{ position: "relative", display: "inline-flex" }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleToggleStep1WorkCenter}
                          disabled={!selectedLocationId}
                          sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            borderRadius: "18px",
                            borderColor: selectedWorkCenterId
                              ? colors.title
                              : "rgba(0,0,0,0.08)",
                            color: selectedWorkCenterId
                              ? colors.title
                              : "rgba(0,0,0,0.6)",
                            fontWeight: selectedWorkCenterId ? 500 : 400,
                            backgroundColor: selectedWorkCenterId
                              ? "rgba(0,0,0,0.04)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: selectedWorkCenterId
                                ? "rgba(0,0,0,0.08)"
                                : "rgba(0,0,0,0.04)",
                              borderColor: selectedWorkCenterId
                                ? colors.title
                                : "rgba(0,0,0,0.08)",
                            },
                          }}
                        >
                          {t("worker-requests_work-center-label", {
                            name:
                              workCenters.find(
                                (wc: { id: number | null }) =>
                                  wc.id === selectedWorkCenterId
                              )?.name || t("worker-requests_all"),
                          })}
                        </Button>
                        <Popover
                          open={Boolean(workCenterAnchorEl)}
                          anchorEl={workCenterAnchorEl}
                          onClose={handleCloseStep1Filters}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                        >
                          <List sx={{ minWidth: 200, maxHeight: 300 }}>
                            <ListItemButton
                              selected={selectedWorkCenterId === null}
                              onClick={() => {
                                setSelectedWorkCenterId(null);
                                handleCloseStep1Filters();
                              }}
                            >
                              <ListItemText
                                primary={t("worker-requests_all-work-centers")}
                              />
                            </ListItemButton>
                            {filteredWorkCenters.map((wc: any) => (
                              <ListItemButton
                                key={wc.id}
                                selected={selectedWorkCenterId === wc.id}
                                onClick={() => {
                                  setSelectedWorkCenterId(wc.id);
                                  handleCloseStep1Filters();
                                }}
                              >
                                <ListItemText primary={wc.name} />
                              </ListItemButton>
                            ))}
                          </List>
                        </Popover>
                      </Box>

                      {(selectedLocationId ||
                        selectedWorkCenterId ||
                        searchQuery) && (
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedLocationId(null);
                            setSelectedWorkCenterId(null);
                            setSearchQuery("");
                          }}
                          sx={{
                            textTransform: "none",
                            color: colors.primary,
                            marginLeft: "auto",
                          }}
                        >
                          {t("worker-requests_clear-all")}
                        </Button>
                      )}
                    </Box>

                    <Divider />

                    <List sx={{ flex: 1, overflowY: "auto" }}>
                      {displayWorkers.length === 0 ? (
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography align="center" color="textSecondary">
                                {t("worker-requests_no-workers-found")}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ) : (
                        displayWorkers.map((worker: any) => {
                          const isSelected =
                            selectedWorker?.workerId === worker.workerId;
                          const initials = `${(
                            worker.firstName?.[0] || ""
                          ).toUpperCase()}${(
                            worker.lastName?.[0] || ""
                          ).toUpperCase()}`;
                          const locationName =
                            locations.find((l) => l.id === worker.locationId)
                              ?.name || "";
                          const workCenterName =
                            workCenters.find(
                              (wc: { id: any }) => wc.id === worker.workCenterId
                            )?.name || "";

                          return (
                            <ListItemButton
                              key={worker.workerId}
                              selected={isSelected}
                              onClick={() => {
                                handleWorkerSelect(null, worker);
                                setStep(2);
                              }}
                              sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                "&:hover": {
                                  bgcolor: "rgba(0, 0, 0, 0.04)",
                                },
                                "&.Mui-selected": {
                                  bgcolor: "rgba(47, 77, 139, 0.08)",
                                  "&:hover": {
                                    bgcolor: "rgba(47, 77, 139, 0.12)",
                                  },
                                },
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    bgcolor: isSelected
                                      ? colors.primary
                                      : "#e0e0e0",
                                    color: isSelected
                                      ? "#fff"
                                      : "rgba(0,0,0,0.87)",
                                    fontSize: "14px",
                                  }}
                                >
                                  {isSelected ? (
                                    <CheckCircleIcon fontSize="small" />
                                  ) : (
                                    initials
                                  )}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Highlight
                                    value={`${worker.firstName} ${worker.lastName}`}
                                    searchText={searchQuery}
                                  />
                                }
                                secondary={
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    <Highlight
                                      value={
                                        worker.badgeNumber ||
                                        worker.companyEmployeeId
                                      }
                                      searchText={searchQuery}
                                    />
                                    {(worker.badgeNumber ||
                                      worker.companyEmployeeId) &&
                                      (locationName || workCenterName) &&
                                      " • "}
                                    <Highlight
                                      value={locationName}
                                      searchText={searchQuery}
                                    />
                                    {locationName && workCenterName && " • "}
                                    <Highlight
                                      value={workCenterName}
                                      searchText={searchQuery}
                                    />
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          );
                        })
                      )}
                    </List>
                  </Box>
                )}
              </Box>
            </Box>
          ) : step === 2 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "auto",
              }}
            >
              <>
                {selectedWorker && (
                  <Box
                    sx={{
                      mx: "45px",
                      mb: 1,
                      p: 2,
                      bgcolor: "#f5f5f5",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: "16px",
                        backgroundColor: "#e0e0e0",
                        border: "1px solid rgba(0,0,0,0.14)",
                        color: "rgba(0,0,0,0.87)",
                      }}
                    >
                      {`${(
                        selectedWorker.firstName?.[0] || ""
                      ).toUpperCase()}${(
                        selectedWorker.lastName?.[0] || ""
                      ).toUpperCase()}`}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "rgba(0,0,0,0.87)",
                        }}
                      >
                        {selectedWorker.firstName} {selectedWorker.lastName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontSize: "12px",
                          color: "rgba(0,0,0,0.6)",
                        }}
                      >
                        {selectedWorker.badgeNumber ||
                          selectedWorker.companyEmployeeId}
                        {(selectedWorker.badgeNumber ||
                          selectedWorker.companyEmployeeId) &&
                          (locations.find(
                            (l: any) => l.id === selectedWorker.locationId
                          ) ||
                            workCenters.find(
                              (wc: any) => wc.id === selectedWorker.workCenterId
                            )) &&
                          " • "}
                        {
                          locations.find(
                            (l: any) => l.id === selectedWorker.locationId
                          )?.name
                        }
                        {locations.find(
                          (l: any) => l.id === selectedWorker.locationId
                        ) &&
                          workCenters.find(
                            (wc: any) => wc.id === selectedWorker.workCenterId
                          ) &&
                          " • "}
                        {
                          workCenters.find(
                            (wc: any) => wc.id === selectedWorker.workCenterId
                          )?.name
                        }
                      </Typography>
                    </Box>
                  </Box>
                )}
                {(rangeStart || rangeEnd) && (
                  <Box
                    sx={{
                      mx: "45px",
                      mb: 1,
                      p: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      bgcolor: "#E8F0FE",
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          color: "#2F4D8B",
                        }}
                      >
                        {t("worker-requests_selected")}
                      </Typography>
                      {rangeStart && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            bgcolor: "white",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            border: "1px solid #2F4D8B",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#2F4D8B",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                            }}
                          >
                            {format(rangeStart, "EEE, d MMM", { locale: dateLocale })}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={clearRangeStart}
                            sx={{ ml: 0.5, p: 0, color: "#2F4D8B" }}
                          >
                            <CloseIcon sx={{ fontSize: "0.875rem" }} />
                          </IconButton>
                        </Box>
                      )}
                      {rangeStart &&
                        rangeEnd &&
                        !isSameDay(rangeStart, rangeEnd) && (
                          <Typography
                            variant="body2"
                            sx={{ color: "#2F4D8B", fontWeight: 500 }}
                          >
                            →
                          </Typography>
                        )}
                      {rangeEnd &&
                        !isSameDay(rangeStart || new Date(), rangeEnd) && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              bgcolor: "white",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              border: "1px solid #2F4D8B",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#2F4D8B",
                                fontWeight: 500,
                                fontSize: "0.75rem",
                              }}
                            >
                              {format(rangeEnd, "EEE, d MMM", { locale: dateLocale })}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={clearRangeEnd}
                              sx={{ ml: 0.5, p: 0, color: "#2F4D8B" }}
                            >
                              <CloseIcon sx={{ fontSize: "0.875rem" }} />
                            </IconButton>
                          </Box>
                        )}
                    </Box>
                    {rangeStart &&
                      rangeEnd &&
                      !isSameDay(rangeStart, rangeEnd) && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            color: "#2F4D8B",
                          }}
                        >
                          {selectedShifts.length}{" "}
                          {selectedShifts.length === 1
                            ? t("worker-requests_shift-singular")
                            : t("worker-requests_shift-plural")}{" "}
                          ·{" "}
                          {Math.max(
                            0,
                            differenceInDays(
                              startOfDay(
                                rangeStart < rangeEnd ? rangeEnd : rangeStart
                              ),
                              startOfDay(
                                rangeStart < rangeEnd ? rangeStart : rangeEnd
                              )
                            ) +
                              1 -
                              selectedShifts.length
                          )}{" "}
                          {t("worker-requests_dates-skipped")}
                        </Typography>
                      )}
                  </Box>
                )}
                <Box
                  sx={{
                    px: "45px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      mt: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#00000099",
                      }}
                    >
                      {workerProfile?.shiftPattern?.name ||
                        selectedWorker.shiftPatternName ||
                        (isLoadingProfile ? "" : t("common_not-applicable"))}
                    </Typography>
                    <CalendarLegend />
                  </Box>
                  {isLoadingCalendarRequests || isLoadingProfile ? (
                    <CalendarSkeleton />
                  ) : (
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={pickerLocale}
                    >
                      <DateCalendar
                        key={currentCalendarMonth.toISOString()}
                        referenceDate={currentCalendarMonth}
                        value={
                          selectedDate &&
                          isSameMonth(selectedDate, currentCalendarMonth)
                            ? selectedDate
                            : null
                        }
                        onMonthChange={(newMonth) => {
                          setCurrentCalendarMonth(newMonth);
                          setExistingRequestDetail(null);
                        }}
                        onYearChange={(newYear) => {
                          setCurrentCalendarMonth(newYear);
                          setExistingRequestDetail(null);
                        }}
                        onChange={() => {
                          /* Handled by renderDay onClick */
                        }}
                        slots={{
                          day: renderDay,
                        }}
                        slotProps={{
                          calendarHeader: {
                            sx: {
                              "& .MuiPickersArrowSwitcher-root": {
                                order: 2,
                                visibility: "visible",
                                zIndex: 1,
                              },
                              "& .MuiPickersCalendarHeader-labelContainer": {
                                order: 1,
                                overflow: "visible",
                                zIndex: 1,
                              },
                              "& .MuiPickersCalendarHeader-root": {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              },
                            },
                          },
                        }}
                        shouldDisableDate={shouldDisableDate}
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          marginBottom: "16px",
                          height: "auto",
                          maxHeight: "none",
                          "& .MuiPickersCalendarHeader-root": {
                            paddingLeft: "24px",
                            paddingRight: "12px",
                            marginBottom: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          },
                          "& .MuiPickersCalendarHeader-labelContainer": {
                            margin: 0,
                          },
                          "& .MuiPickersCalendarHeader-label": {
                            fontSize: "1.1rem",
                            fontWeight: "500",
                            fontFamily: "Roboto",
                          },
                          "& .MuiPickersArrowSwitcher-root": {
                            display: "flex",
                            zIndex: 1,
                          },
                          "& .MuiPickersFadeTransitionGroup-root": {
                            height: "auto",
                          },
                          "& .MuiDayCalendar-slideTransition": {
                            minHeight: "none",
                            height: "auto",
                          },
                          "& .MuiDayCalendar-header": {
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 1fr)",
                            gap: "2px",
                            "& > span:nth-of-type(6), & > span:nth-of-type(7)":
                              {
                                color: "#00000061",
                              },
                          },
                          "& .MuiDayCalendar-monthContainer": {
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                          },
                          "& .MuiDayCalendar-weekContainer": {
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 1fr)",
                            gap: "2px",
                            margin: "0",
                            "& .MuiPickersDay-dayPlaceholder": {
                              width: "100%",
                              height: "46px",
                            },
                          },
                          "& .MuiPickersDay-root": {
                            width: "100%",
                            height: "46px",
                            fontSize: "1rem",
                          },
                          "& .MuiDayCalendar-weekDayLabel": {
                            width: "100%",
                            height: "46px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.875rem",
                            color: "#00000099",
                            fontFamily: "Roboto",
                            margin: 0,
                          },
                          "& .MuiYearCalendar-root": {
                            width: "100%",
                            maxWidth: "100%",
                          },
                          "& .MuiPickersYear-root": {
                            flexBasis: "33.33%",
                            maxWidth: "33.33%",
                          },
                          "& .MuiPickersYear-yearButton": {
                            width: "100%",
                            height: "46px",
                            fontSize: "1rem",
                          },
                          "& .MuiMonthCalendar-root": {
                            width: "100%",
                            maxWidth: "100%",
                          },
                          "& .MuiPickersMonth-root": {
                            flexBasis: "33.33%",
                            maxWidth: "33.33%",
                          },
                          "& .MuiPickersMonth-monthButton": {
                            width: "100%",
                            height: "46px",
                            fontSize: "1rem",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                  {existingRequestDetail && (
                    <Box
                      ref={existingRequestRef}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: "1px solid #FFB74D",
                        bgcolor: "#FFF3E0",
                        borderRadius: 1,
                        position: "relative",
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        onClick={() => setExistingRequestDetail(null)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: "Roboto",
                          color: "#E65100",
                          fontWeight: "bold",
                          mb: 0.5,
                        }}
                      >
                        {t("worker-requests_existing-time-off-on", {
                          date: format(
                            new Date(
                              existingRequestDetail.startDate ||
                                existingRequestDetail.ftShift?.startDateTime
                            ),
                            "EEE, d MMM",
                            { locale: dateLocale }
                          ),
                        })}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "Roboto" }}
                          >
                            {existingRequestDetail.leaveTypes &&
                            existingRequestDetail.leaveTypes.length > 0
                              ? existingRequestDetail.leaveTypes
                                  .map((lt: any) => {
                                    const hoursStr =
                                      getHoursAndMinutesStringFromFloat(
                                        lt.requestedHours || 0
                                      );
                                    return `${hoursStr} · ${
                                      lt.leaveTypeName ||
                                      lt.name ||
                                      t("worker-requests_time-off")
                                    }`;
                                  })
                                  .join(" + ")
                              : (existingRequestDetail.leaveTypeDisplay &&
                                  formatLeaveTypeDisplay(
                                    existingRequestDetail.leaveTypeDisplay
                                  )) ||
                                (existingRequestDetail.leaveTypes?.[0]
                                  ?.leaveTypeName ||
                                  existingRequestDetail.leaveTypes?.[0]?.name ||
                                  t("worker-requests_time-off")) +
                                  (existingRequestDetail.leaveTypes?.[0]
                                    ?.requestedHours
                                    ? ` · ${getHoursAndMinutesStringFromFloat(
                                        existingRequestDetail.leaveTypes[0]
                                          .requestedHours
                                      )}`
                                    : "")}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Roboto",
                              color: "rgba(0,0,0,0.6)",
                            }}
                          >
                            {t("worker-requests_requested-on-date", {
                              date: format(
                                new Date(
                                  existingRequestDetail.createdDate || new Date()
                                ),
                                "d MMM",
                                { locale: dateLocale }
                              ),
                            })}{" "}
                            ·{" "}
                            {existingRequestDetail.status?.name ||
                              t("worker-requests_pending-approval")}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
                {!isResolvingShiftId &&
                  selectedDate &&
                  isSameMonth(selectedDate, currentCalendarMonth) &&
                  !resolvedShiftId &&
                  !isLoadingProfile && (
                    <Box sx={{ px: 3, pb: 2, pt: 2 }}>
                      <Alert severity="warning">
                        {t("worker-requests_no-matching-shift")}
                      </Alert>
                    </Box>
                  )}
              </>
            </Box>
          ) : step === 3 && selectedShifts.length > 1 ? (
            <Box>
              <Box
                sx={{
                  padding: "45px 45px 16px 45px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "20px",
                    lineHeight: "32px",
                    color: "#000000DE",
                  }}
                >
                  {t("worker-requests_mark-as-time-off", {
                    name: `${selectedWorker.firstName} ${selectedWorker.lastName}`,
                  })}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "4px",
                      alignItems: "center",
                    }}
                  >
                    <BadgeOutlinedIcon
                      sx={{ color: "#0000008f", fontSize: "16px" }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#00000099",
                      }}
                    >
                      {workerProfile?.badgeNumber ||
                        workerProfile?.companyEmployeeId ||
                        selectedWorker.badgeNumber ||
                        selectedWorker.companyEmployeeId ||
                        (isLoadingProfile ? "" : t("common_not-applicable"))}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "4px",
                      alignItems: "center",
                    }}
                  >
                    <LocationOnOutlinedIcon
                      sx={{ color: "#0000008f", fontSize: "16px" }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#00000099",
                      }}
                    >
                      {workerProfile?.location?.name ||
                        locations.find((l) => l.id === selectedLocationId)
                          ?.name ||
                        (isLoadingProfile ? "" : t("common_not-applicable"))}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "4px",
                      alignItems: "center",
                    }}
                  >
                    <BuildOutlinedIcon
                      sx={{ color: "#0000008f", fontSize: "16px" }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#00000099",
                      }}
                    >
                      {workerProfile?.workCenter?.name ||
                        selectedWorker.workCenterName ||
                        (isLoadingProfile ? "" : t("common_not-applicable"))}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ padding: "0 45px" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#00000099",
                    }}
                  >
                    {selectedShiftPatternNames.join(", ") ||
                      workerProfile?.shiftPattern?.name ||
                      selectedWorker.shiftPatternName ||
                      (isLoadingProfile ? "" : t("common_not-applicable"))}
                    {(selectedShiftPatternNames.length > 0 ||
                      workerProfile?.shiftPattern?.name ||
                      selectedWorker.shiftPatternName ||
                      !isLoadingProfile) &&
                      ` - ${t("worker-requests_selected-shifts")}`}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#000000DE",
                      mr: 5,
                    }}
                  >
                    {t("worker-requests_shifts-count", {
                      count: selectedShifts.length,
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{ border: "1px solid #E0E0E0", borderRadius: 1, mb: 3 }}
                >
                  <List
                    disablePadding
                    sx={{ maxHeight: 200, overflow: "auto" }}
                  >
                    {selectedShifts.map((shift, index) => {
                      const start = new Date(shift.startDateTime);
                      const end = new Date(shift.endDateTime);
                      const hours =
                        (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                      const hoursAndMinutesStr =
                        getHoursAndMinutesStringFromFloat(hours);
                      return (
                        <React.Fragment key={shift.shiftId}>
                          <ListItem
                            sx={{
                              py: 1,
                              px: 2,
                            }}
                          >
                            <ListItemText
                              primary={`${format(
                                start,
                                "EEE, d MMM",
                                { locale: dateLocale }
                              )} · ${format(start, "h:mm a")} – ${format(
                                end,
                                "h:mm a"
                              )}`}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontFamily: "Roboto", fontSize: "14px" },
                              }}
                              secondary={
                                hasMultiplePatternsSelected
                                  ? getPatternName(shift.uPatternId)
                                  : undefined
                              }
                              secondaryTypographyProps={{
                                variant: "caption",
                                sx: { fontFamily: "Roboto" },
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "Roboto", mr: 5 }}
                            >
                              {hoursAndMinutesStr}
                            </Typography>
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() =>
                                  handleRemoveShiftFromBatch(index)
                                }
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < selectedShifts.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                  <Divider />
                  <Box
                    sx={{
                      bgcolor: "#FAFAFA",
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      {t("worker-requests_total")}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: "bold",
                        fontSize: "14px",
                        mr: 5,
                      }}
                    >
                      {getHoursAndMinutesStringFromFloat(totalSelectedHours)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: "24px", mb: 3 }}>
                  <FormControl sx={{ flex: 2 }}>
                    <TextField
                      select
                      label={t("worker-requests_time-off-type")}
                      value={selectedLeaveTypeId || ""}
                      onChange={handleLeaveTypeChange}
                      fullWidth
                      variant="filled"
                      slotProps={{
                        select: {
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: "200px",
                              },
                            },
                          },
                          renderValue: (value: any) => {
                            const callOff = workerLeaveBalances.find(
                              (b) => b.id === Number(value)
                            );
                            if (!callOff) return null;
                            return (
                              <Typography
                                sx={{
                                  fontFamily: "Roboto",
                                  fontWeight: 400,
                                  fontSize: "16px",
                                }}
                              >
                                {callOff.name}
                                {!callOff.isNoLimit &&
                                  ` (${getHoursAndMinutesStringFromFloat(
                                    callOff.availableHrs ?? 0
                                  )})`}
                              </Typography>
                            );
                          },
                        },
                      }}
                    >
                      {workerLeaveBalances.map((callOff) => (
                        <MenuItem key={callOff.id} value={callOff.id}>
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "16px",
                            }}
                          >
                            {callOff.name}
                            {!callOff.isNoLimit &&
                              ` (${getHoursAndMinutesStringFromFloat(
                                callOff.availableHrs ?? 0
                              )})`}
                          </Typography>
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <Autocomplete
                      ListboxProps={{ style: { maxHeight: "200px" } }}
                      options={durationOptions}
                      getOptionLabel={(option) => option.text}
                      value={
                        durationOptions.find(
                          (o) =>
                            Math.round(o.timeInMinutes) ===
                            Math.round(duration ?? 0)
                        ) ||
                        (duration
                          ? {
                              text: getHoursAndMinutesStringFromFloat(
                                (duration ?? 0) / 60
                              ),
                              timeInMinutes: duration,
                            }
                          : null)
                      }
                      onChange={(_event, newValue) => {
                        const newDuration = newValue
                          ? newValue.timeInMinutes
                          : 0;
                        setDuration(newDuration);
                        if (isPairingAccordionOpen) {
                          setDuration2(
                            Math.max(0, totalSelectedHours * 60 - newDuration)
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("worker-requests_duration")}
                          variant="filled"
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Box>

                {showPairingAccordion && (
                  <Box
                    sx={{
                      backgroundColor: colors.grey,
                      borderRadius: "4px",
                      marginBottom: "16px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingLeft: "12px",
                        paddingRight: "8px",
                        paddingTop: "16px",
                        paddingBottom: "16px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setIsPairingAccordionOpen(!isPairingAccordionOpen);
                        if (isPairingAccordionOpen) {
                          setSelectedLeaveTypeId2(null);
                          setDuration2(0);
                          setDuration(totalSelectedHours * 60);
                        }
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontSize: "14px",
                          fontWeight: 400,
                          color: colors.title,
                        }}
                      >
                        {isPairingAccordionOpen
                          ? t("worker-requests_remove-second-time-off")
                          : t("worker-requests_add-second-time-off")}
                      </Typography>
                      {isPairingAccordionOpen ? (
                        <RemoveOutlinedIcon
                          sx={{
                            color: colors.title,
                            fontSize: "20px",
                          }}
                        />
                      ) : (
                        <AddOutlinedIcon
                          sx={{
                            color: colors.title,
                            fontSize: "20px",
                          }}
                        />
                      )}
                    </Box>
                    {isPairingAccordionOpen && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: "24px",
                          padding: "12px",
                          backgroundColor: colors.greyLight,
                        }}
                      >
                        <FormControl sx={{ flex: 2 }}>
                          <TextField
                            select
                            label={t("worker-requests_time-off-type")}
                            value={selectedLeaveTypeId2 || ""}
                            onChange={(e) =>
                              setSelectedLeaveTypeId2(Number(e.target.value))
                            }
                            fullWidth
                            variant="filled"
                            slotProps={{
                              select: {
                                MenuProps: {
                                  PaperProps: {
                                    style: {
                                      maxHeight: "200px",
                                    },
                                  },
                                },
                                renderValue: (value: any) => {
                                  const callOff = workerLeaveBalances.find(
                                    (b) => b.id === Number(value)
                                  );
                                  if (!callOff) return null;
                                  return (
                                    <Typography
                                      sx={{
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "16px",
                                      }}
                                    >
                                      {callOff.name}
                                      {!callOff.isNoLimit &&
                                        ` (${getHoursAndMinutesStringFromFloat(
                                          callOff.availableHrs ?? 0
                                        )})`}
                                    </Typography>
                                  );
                                },
                              },
                            }}
                          >
                            {workerLeaveBalances
                              .filter(
                                (b) =>
                                  b.id !== selectedLeaveTypeId &&
                                  b.pairingEnabled
                              )
                              .map((callOff) => (
                                <MenuItem key={callOff.id} value={callOff.id}>
                                  <Typography
                                    sx={{
                                      fontFamily: "Roboto",
                                      fontWeight: 400,
                                      fontSize: "16px",
                                    }}
                                  >
                                    {callOff.name}
                                    {!callOff.isNoLimit &&
                                      ` (${getHoursAndMinutesStringFromFloat(
                                        callOff.availableHrs ?? 0
                                      )})`}
                                  </Typography>
                                </MenuItem>
                              ))}
                          </TextField>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                          <Autocomplete
                            ListboxProps={{ style: { maxHeight: "200px" } }}
                            options={durationOptions2}
                            getOptionLabel={(option) => option.text}
                            value={
                              durationOptions2.find(
                                (o) =>
                                  Math.round(o.timeInMinutes) ===
                                  Math.round(duration2 ?? 0)
                              ) ||
                              (duration2
                                ? {
                                    text: getHoursAndMinutesStringFromFloat(
                                      (duration2 ?? 0) / 60
                                    ),
                                    timeInMinutes: duration2,
                                  }
                                : null)
                            }
                            onChange={(_event, newValue) => {
                              const newDuration2 = newValue
                                ? newValue.timeInMinutes
                                : 0;
                              setDuration2(newDuration2);
                              setDuration(
                                Math.max(
                                  0,
                                  totalSelectedHours * 60 - newDuration2
                                )
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t("worker-requests_duration")}
                                variant="filled"
                                fullWidth
                              />
                            )}
                          />
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                )}

                {((selectedLeaveTypeId &&
                  !workerLeaveBalances.find((b) => b.id === selectedLeaveTypeId)
                    ?.isNoLimit &&
                  (workerLeaveBalances.find((b) => b.id === selectedLeaveTypeId)
                    ?.availableHrs ?? 0) <
                    (duration ?? 0) / 60 &&
                  !selectedLeaveTypeId2) ||
                  (selectedLeaveTypeId2 &&
                    !workerLeaveBalances.find(
                      (b) => b.id === selectedLeaveTypeId
                    )?.isNoLimit &&
                    !workerLeaveBalances.find(
                      (b) => b.id === selectedLeaveTypeId2
                    )?.isNoLimit &&
                    (workerLeaveBalances.find(
                      (b) => b.id === selectedLeaveTypeId
                    )?.availableHrs ?? 0) +
                      (workerLeaveBalances.find(
                        (b) => b.id === selectedLeaveTypeId2
                      )?.availableHrs ?? 0) <
                      ((duration ?? 0) + (duration2 ?? 0)) / 60)) && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="warning">
                      {t("worker-requests_insufficient-balance-inline")}
                    </Alert>
                  </Box>
                )}

                <Box
                  sx={{
                    mb: 3,
                    position: "relative",
                    marginTop: "16px",
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={callOffReason}
                    onChange={(e) =>
                      setCallOffReason(e.target.value.slice(0, 50))
                    }
                    placeholder={t("worker-requests_reason-for-absence")}
                    variant="filled"
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      "& .MuiFilledInput-root": {
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "16px",
                        backgroundColor: "#FAFAFA",
                      },
                      marginBottom: "20px",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-20px",
                      right: 0,
                      fontSize: "12px",
                      color: "rgba(0, 0, 0, 0.6)",
                      fontFamily: "Roboto",
                    }}
                  >
                    {callOffReason.length} / 50
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <CallOffSection
              workerId={selectedWorker.workerId}
              workerFullName={`${selectedWorker.firstName} ${selectedWorker.lastName}`}
              fromStatus={selectedWorker.status || "SCHEDULED"}
              currentAssignment={callOffSectionCurrentAssignment}
              shiftDetails={callOffSectionShiftDetails}
              onConfirm={handleConfirm}
              setCalledOffData={setTempCallOffData}
              onCancel={handleClose}
              onBack={handleBack}
              showFooter={false}
            />
          )}
        </DialogContent>
        {step !== 4 && (
          <DialogActions
            sx={{
              padding: "20px 45px",
              gap: "8px",
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid #E0E0E0",
              backgroundColor: "#fff",
            }}
          >
            {step === 1 && (
              <>
                <Button
                  onClick={handleClose}
                  variant="text"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "64px",
                    padding: "8px 16px",
                  }}
                >
                  {t("common_cancel")}
                </Button>
                <Button
                  variant="contained"
                  disabled={!selectedWorker}
                  onClick={handleNext}
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: 160,
                    padding: "8px 16px",
                  }}
                >
                  {t("worker-requests_next")}
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                <Button
                  onClick={handleClose}
                  variant="text"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "64px",
                    padding: "8px 16px",
                  }}
                >
                  {t("common_cancel")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "64px",
                    padding: "8px 16px",
                  }}
                >
                  {t("worker-requests_back")}
                </Button>
                <Button
                  variant="contained"
                  disabled={
                    (!selectedDate && selectedShifts.length === 0) ||
                    isResolvingShiftId ||
                    (selectedDate && !resolvedShiftId) ||
                    !!rangeError
                  }
                  onClick={handleNext}
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: 160,
                    padding: "8px 16px",
                  }}
                >
                  {isResolvingShiftId ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("worker-requests_next")
                  )}
                </Button>
              </>
            )}
            {step === 3 && selectedShifts.length > 1 && (
              <>
                <Button
                  onClick={handleClose}
                  variant="text"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "64px",
                    padding: "8px 16px",
                  }}
                >
                  {t("common_cancel")}
                </Button>
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "64px",
                    padding: "8px 16px",
                  }}
                >
                  {t("worker-requests_back")}
                </Button>
                <Button
                  variant="contained"
                  disabled={
                    isSubmitting ||
                    selectedShifts.length === 0 ||
                    !selectedLeaveTypeId
                  }
                  onClick={handleBatchConfirm}
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "160px",
                    padding: "8px 16px",
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("worker-requests_confirm-time-off-count", {
                      count: selectedShifts.length,
                    })
                  )}
                </Button>
              </>
            )}
            {step === 3 && selectedShifts.length <= 1 && (
              <>
                <Button
                  onClick={handleClose}
                  variant="text"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "64px",
                    padding: "8px 16px",
                  }}
                >
                  {t("common_cancel")}
                </Button>
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "64px",
                    padding: "8px 16px",
                  }}
                >
                  {t("worker-requests_back")}
                </Button>
                <Button
                  variant="contained"
                  disabled={
                    isSubmitting ||
                    !tempCallOffData ||
                    !tempCallOffData.ftRequestTypeId ||
                    tempCallOffData.ftLeaveTypes?.length === 0 ||
                    !tempCallOffData.ftEndDateTime
                  }
                  onClick={handleConfirm}
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    textTransform: "uppercase",
                    minWidth: "160px",
                    padding: "8px 16px",
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("worker-requests_confirm-time-off")
                  )}
                </Button>
              </>
            )}
          </DialogActions>
        )}
        <Snackbar
          open={!!rangeError}
          autoHideDuration={6000}
          onClose={() => setRangeError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => setRangeError(null)}>
            {rangeError}
          </Alert>
        </Snackbar>
        {apiError && (
          <Snackbar
            open={!!apiError}
            autoHideDuration={3000}
            onClose={() => setApiError(null)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity="error" onClose={() => setApiError(null)}>
              {apiError}
            </Alert>
          </Snackbar>
        )}
      </Dialog>
    </React.Fragment>
  );
};

export default NewWorkerRequestDialog;
